import { useEffect, useRef, type ReactNode } from 'react';
import {
  DndContext,
  KeyboardSensor,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { type OrgEventChecklistItemDef } from 'hooks/orgEvents/orgEventAutomationHooks';
import {
  checklistKindLabel,
  checklistSubtitle,
  filterChecklistItems,
} from './checklistSubtitles';

const BASE_COLUMN_COUNT = 3;

/**
 * Searchable table of the response checklist items on a rule.
 * `renderLeadingRow` carries the inline add row, which is why the table still
 * renders when there are no items yet.
 */
export function ChecklistItemTable({
  items,
  userNameById,
  search,
  onSearchChange,
  searchLabel,
  emptyMessage,
  renderAction,
  onSelect,
  renderLeadingRow,
  toolbar,
  reorderable = false,
  onReorder,
}: {
  items: OrgEventChecklistItemDef[];
  userNameById: Map<string, string>;
  search: string;
  onSearchChange: (next: string) => void;
  searchLabel: string;
  /** Shown instead of the table when there is nothing to search or add. */
  emptyMessage: string;
  renderAction: (item: OrgEventChecklistItemDef) => ReactNode;
  /** Makes the label cell clickable, for opening an item's full template. */
  onSelect?: (item: OrgEventChecklistItemDef) => void;
  /** Full-width row pinned above the items, for adding a new one. */
  renderLeadingRow?: () => ReactNode;
  /** Sits between the search field and the table, left aligned. */
  toolbar?: ReactNode;
  reorderable?: boolean;
  onReorder?: (next: OrgEventChecklistItemDef[]) => void;
}) {
  const leadingRow = renderLeadingRow?.();
  const hasLeadingRow = Boolean(leadingRow);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // The add row sits at the top of a scrolling list, so reveal it rather than
  // leaving the user staring at wherever they had scrolled to.
  useEffect(() => {
    if (hasLeadingRow) scrollRef.current?.scrollTo({ top: 0 });
  }, [hasLeadingRow]);

  const filtered = filterChecklistItems(items, search, userNameById);
  const columnCount = BASE_COLUMN_COUNT + (reorderable ? 1 : 0);
  const showTable = items.length > 0 || hasLeadingRow;

  const handleDragEnd = (event: DragEndEvent) => {
    const overId = event.over?.id;
    if (!overId || overId === event.active.id || !onReorder) return;
    const from = items.findIndex((item) => item.id === event.active.id);
    const to = items.findIndex((item) => item.id === overId);
    if (from < 0 || to < 0) return;
    onReorder(arrayMove(items, from, to));
  };

  return (
    <div
      className={cn('tw:flex tw:flex-col', toolbar ? 'tw:gap-4' : 'tw:gap-2')}
    >
      {items.length > 0 ? (
        <Input
          value={search}
          onChange={(e) => onSearchChange(e.target.value)}
          placeholder={searchLabel}
          aria-label={searchLabel}
          className="tw:h-8"
        />
      ) : null}
      {toolbar ? (
        <div className="tw:flex tw:justify-start">{toolbar}</div>
      ) : null}
      {showTable ? (
        <div
          ref={scrollRef}
          className="tw:max-h-56 tw:overflow-y-auto tw:rounded-md tw:border tw:border-border"
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <Table>
              <TableHeader>
                <TableRow>
                  {reorderable ? (
                    <TableHead className="tw:w-8 tw:px-1" />
                  ) : null}
                  <TableHead className="tw:w-[140px]">Type</TableHead>
                  <TableHead>Item</TableHead>
                  <TableHead className="tw:w-[52px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {leadingRow ? (
                  <TableRow className="tw:hover:bg-transparent">
                    <TableCell
                      colSpan={columnCount}
                      className="tw:whitespace-normal"
                    >
                      {leadingRow}
                    </TableCell>
                  </TableRow>
                ) : null}
                {items.length === 0 ? null : filtered.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      className="tw:text-xs tw:text-muted-foreground"
                    >
                      No items match “{search}”.
                    </TableCell>
                  </TableRow>
                ) : (
                  <SortableContext
                    items={filtered.map((item) => item.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    {filtered.map((item) => (
                      <SortableChecklistRow
                        key={item.id}
                        item={item}
                        reorderable={reorderable}
                        userNameById={userNameById}
                        onSelect={onSelect}
                        renderAction={renderAction}
                      />
                    ))}
                  </SortableContext>
                )}
              </TableBody>
            </Table>
          </DndContext>
        </div>
      ) : (
        <p className="tw:m-0 tw:text-xs tw:text-muted-foreground">
          {emptyMessage}
        </p>
      )}
    </div>
  );
}

function SortableChecklistRow({
  item,
  reorderable,
  userNameById,
  onSelect,
  renderAction,
}: {
  item: OrgEventChecklistItemDef;
  reorderable: boolean;
  userNameById: Map<string, string>;
  onSelect?: (item: OrgEventChecklistItemDef) => void;
  renderAction: (item: OrgEventChecklistItemDef) => ReactNode;
}) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: item.id, disabled: !reorderable });

  return (
    <TableRow
      ref={setNodeRef}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
      className={isDragging ? 'tw:opacity-50' : undefined}
    >
      {reorderable ? (
        <TableCell className="tw:w-8 tw:px-1 tw:align-top">
          <button
            type="button"
            className="tw:inline-flex tw:cursor-grab tw:items-center tw:justify-start tw:p-0 tw:text-muted-foreground tw:active:cursor-grabbing"
            aria-label={`Reorder ${item.label}`}
            {...attributes}
            {...listeners}
          >
            <GripVertical className="tw:size-4" />
          </button>
        </TableCell>
      ) : null}
      <TableCell className="tw:align-top">
        <Badge variant="outline" className="tw:text-[10px]">
          {checklistKindLabel(item)}
        </Badge>
      </TableCell>
      <TableCell className="tw:align-top">
        {onSelect ? (
          <button
            type="button"
            className="tw:min-w-0 tw:bg-transparent tw:p-0 tw:text-left"
            onClick={() => onSelect(item)}
          >
            <ChecklistItemLabel item={item} userNameById={userNameById} />
          </button>
        ) : (
          <ChecklistItemLabel item={item} userNameById={userNameById} />
        )}
      </TableCell>
      <TableCell className="tw:align-top">{renderAction(item)}</TableCell>
    </TableRow>
  );
}

function ChecklistItemLabel({
  item,
  userNameById,
}: {
  item: OrgEventChecklistItemDef;
  userNameById: Map<string, string>;
}) {
  return (
    <>
      <span className="tw:text-sm tw:leading-snug">{item.label}</span>
      <p className="tw:m-0 tw:mt-0.5 tw:text-[11px] tw:text-muted-foreground">
        {checklistSubtitle(item, userNameById)}
      </p>
    </>
  );
}
